import React, { Suspense, useContext, useState } from 'react';
import { arrayOf, number, shape, string } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Carousel } from '@thd-olt-component-react/carousel';
import { Row, Col } from '@thd-olt-component-react/grid';
import { Heading } from '@thd-olt-component-react/heading';
import { Image } from '@thd-olt-component-react/image';

const Overlay = React.lazy(() => import(/* webpackChunkName: "overlay" */ '@thd-olt-component-react/overlay')
  .then((module) => ({ default: module.Overlay })));

const RelatedSearchProducts = (props) => {
  const {
    relatedSearchProducts,
    maxRelatedProducts,
    relatedProductTitle
  } = props;

  const {
    channel
  } = useContext(ExperienceContext);

  const relatedProductsToShow = [...relatedSearchProducts].slice(0, maxRelatedProducts);

  const handleMoreClick = (index) => {
    const relatedProductPods = document.querySelectorAll('.related-products__pod');
    relatedProductPods.forEach((pod, i) => {
      if (i !== index) {
        pod.classList.add('hide-rel-prod');
      } else {
        pod.classList.add('largeView');
      }
    });
  };

  const handleCloseClick = () => {
    const relatedProductPods = document.querySelectorAll('.related-products__pod');
    relatedProductPods.forEach((pod, i) => {
      pod.classList.remove('hide-rel-prod');
      pod.classList.remove('largeView');
    });
  };

  return (
    <Row id="related-products" className="related-products">
      {channel !== 'mobile' && (
        <Col>
          <Heading underline centered title={relatedProductTitle} />

          <div className="related-products__set">
            {(relatedProductsToShow || []).map((product, index) => {
              const { details, identifiers, media } = product || {};
              const { description } = details || {};
              const { canonicalUrl, productLabel } = identifiers || {};
              const { images } = media || {};
              const image = images?.[0];

              return (
                <div key={`relatedProduct-${image?.url}-${index}`} className="related-products__pod">
                  <div className="related-products__image">
                    <a href={canonicalUrl}>
                      {image?.url && (
                        <Image
                          stretchy
                          lazy
                          src={`${image?.url}`.replace(/<SIZE>/g, '300')}
                          alt={`${productLabel}`.replace(/"/g, '\'\'')}
                          width={1}
                          height={1}
                        />
                      )}
                    </a>
                  </div>
                  <div className="related-products__content">
                    <h3 className="related-products__title"> {productLabel} </h3>
                    <span
                      className="related-products__short-description"
                    >
                      {description.replace(/^(.{40}[^\s]*).*/, '$1')}
                    </span>
                    <span className="related-products__description">{description}</span>
                    <div>
                      {/* eslint-disable-next-line max-len */}
                      <button className="related-products__more-link" type="button" onClick={() => handleMoreClick(index)}>
                        <span className="related-products__more">View More</span>
                      </button>
                    </div>
                    <a
                      className="related-products__product-details"
                      href={canonicalUrl}
                    >View Full Product Description
                    </a>
                  </div>
                  <button
                    className="related-products__largeView-close"
                    type="button"
                    onClick={handleCloseClick}
                  >
                    <span>Close</span>
                    <i className="related-products__close" />
                  </button>
                </div>
              );
            })}
          </div>
        </Col>
      )}
      {channel === 'mobile' && (
        <Col>
          <Heading underline title={relatedProductTitle} />
          <Carousel showArrows>
            {(relatedProductsToShow || []).map((product, index) => {
              const { details, identifiers, media } = product || {};
              const { description } = details || {};
              const { canonicalUrl, productLabel } = identifiers || {};
              const { images } = media || {};
              const image = images?.[0];
              // eslint-disable-next-line react-hooks/rules-of-hooks
              const [isOverlayOpen, setIsOverlayOpen] = useState(false);
              const toolTip = (
                <div className="related-products__overlay">
                  <div className="related-products__image">
                    <a href={canonicalUrl}>
                      {image?.url && (
                        <Image
                          lazy
                          stretchy
                          src={`${image?.url}`.replace(/<SIZE>/g, '300')}
                          alt={`${productLabel}`.replace(/"/g, '\'\'')}
                          width={1}
                          height={1}
                        />
                      )}
                    </a>
                  </div>
                  <div className="related-products__title"> {productLabel} </div>
                  <div className="related-products__description">{description}</div>
                  <a className="related-products__product-details" href={canonicalUrl}>View Full Product Description</a>
                </div>
              );
              return (
                <div
                  key={`relatedProduct-${image?.url}-${index}`}
                  className="related-products__pod"
                >
                  <div className="related-products__image">
                    <a href={canonicalUrl}>
                      {image?.url && (
                        <Image
                          stretchy
                          lazy
                          src={`${image?.url}`.replace(/<SIZE>/g, '300')}
                          alt={`${productLabel}`.replace(/"/g, '\'\'')}
                          width={1}
                          height={1}
                        />
                      )}
                    </a>
                  </div>
                  <div className="related-products__content">
                    <div className="related-products__title"> {productLabel} </div>
                    <span className="related-products__short-description">
                      {description.replace(/^(.{40}[^\s]*).*/, '$1')}
                    </span>
                    <button
                      type="button"
                      className="related-products__more-link"
                      onClick={() => setIsOverlayOpen(true)}
                    >View More
                    </button>
                    {isOverlayOpen && (
                      <Suspense fallback={(<div />)}>
                        <Overlay
                          open={isOverlayOpen}
                          onClose={() => setIsOverlayOpen(false)}
                          closeButton
                        >
                          {toolTip}
                        </Overlay>
                      </Suspense>
                    )}
                  </div>
                </div>
              );
            })}
          </Carousel>
        </Col>
      )}
    </Row>
  );
};

RelatedSearchProducts.defaultProps = {
  maxRelatedProducts: 4,
  relatedProductTitle: 'Related Products',
  relatedSearchProducts: []
};

RelatedSearchProducts.propTypes = {
  maxRelatedProducts: number,
  relatedProductTitle: string,
  relatedSearchProducts: arrayOf(shape({}))
};

export { RelatedSearchProducts };
